import React, { useEffect, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { useSalesEntry } from 'contexts/SalesEntryContext';

interface TabConfig {
  id: string;
  label: string;
  icon: string;
  shortcut: string;
  onClick: () => void;
  getValue: () => { count: number; display: string };
}

interface SalesEntryTabsProps {
  onCustomerClick: () => void;
  onSalesPersonClick: () => void;
  onCouponClick: () => void;
  onDiscountClick: () => void;
  onPaymentClick: () => void;
  onOldGoldClick: () => void;
  onExchangeClick: () => void;
  onRedemptionClick: () => void;
  onRewardPointsClick: () => void;
  onCalculationClick: () => void;
}

export const SalesEntryTabs: React.FC<SalesEntryTabsProps> = ({
  onCustomerClick,
  onSalesPersonClick,
  onCouponClick,
  onDiscountClick,
  onPaymentClick,
  onOldGoldClick,
  onExchangeClick,
  onRedemptionClick,
  onRewardPointsClick,
  onCalculationClick
}) => {
  const { state } = useSalesEntry();

  // Tab configuration array - memoized to prevent re-creation on every render
  const tabConfigs: TabConfig[] = useMemo(() => [
    {
      id: 'customer',
      label: 'Customer',
      icon: 'bx-user',
      shortcut: 'F1',
      onClick: onCustomerClick,
      getValue: () => ({
        count: state.customer ? 1 : 0,
        display:
          state.customer?.BillingAddressId === null ||
            state.customer?.BillingAddressId === undefined
            ? ''
            : typeof state.customer?.BillingAddressId === 'number'
              ? state.customer.BillingAddressId.toString()
              : state.customer.BillingAddressId
      })
    },
    {
      id: 'salesPerson',
      label: 'Sales Person',
      icon: 'bx-user-check',
      shortcut: 'F2',
      onClick: onSalesPersonClick,
      getValue: () => ({
        count: state.salesPerson ? 1 : 0,
        display: state.salesPerson ? state.salesPerson.name : ''
      })
    },
    {
      id: 'coupon',
      label: 'Coupon',
      icon: 'bx-receipt',
      shortcut: 'F3',
      onClick: onCouponClick,
      getValue: () => ({
        count: state.coupon ? 1 : 0,
        display: state.coupon ? state.coupon.code : ''
      })
    },
    {
      id: 'discount',
      label: 'Discount',
      icon: 'bx-percentage',
      shortcut: 'F7',
      onClick: onDiscountClick,
      getValue: () => ({
        count: state.discount > 0 ? 1 : 0,
        display: state.discount > 0 ? `₹${state.discount}` : ''
      })
    },
    {
      id: 'oldGold',
      label: 'Old Gold',
      icon: 'bx-crown',
      shortcut: 'F12',
      onClick: onOldGoldClick,
      getValue: () => ({
        count: state.oldGoldEntries?.length || 0,
        display: state.oldGoldEntries?.length > 0 ?
          `₹${state.oldGoldEntries.reduce((sum, entry) => sum + entry.totalAmt, 0).toLocaleString()}` :
          ''
      })
    },
    {
      id: 'exchange',
      label: 'Exchange',
      icon: 'bx-transfer',
      shortcut: '',
      onClick: onExchangeClick,
      getValue: () => ({
        count: state?.exchangeSalesReturnData?.returnSalesOrderModel?.totalReturnSaleOrderAmount ? 1 : 0,
        display: state.exchange ? `₹${state.exchange.totalAmount.toLocaleString()}` : ''
      })
    },
    // {
    //   id: 'redemption',
    //   label: 'Redemption',
    //   icon: 'bx-gift',
    //   shortcut: '',
    //   onClick: onRedemptionClick,
    //   getValue: () => ({
    //     count: state.redemption ? 1 : 0,
    //     display: state.redemption ? `${state.redemption.points} pts` : ''
    //   })
    // },
    {
      id: 'rewardPoints',
      label: 'Reward Points',
      icon: 'bx-medal',
      shortcut: 'F9',
      onClick: onRewardPointsClick,
      getValue: () => {
        const hasRewardDis = state.items.some(item => item.rewardDis);
        return {
          count: state.rewardPoints > 0 ? 1 : 0,
          display: state.rewardPoints > 0 ? `${state.rewardPoints} pts` : '',
          rewardDisAvailable: hasRewardDis ? "true" : "false"
        };
      }
    },
    {
      id: 'payment',
      label: 'Payment',
      icon: 'bx-credit-card',
      shortcut: 'F8',
      onClick: onPaymentClick,
      getValue: () => {
        const totalAmount = state.payment?.reduce((sum, p) => sum + p.amount, 0) || 0;
        const count = state.payment?.length || 0;
        return {
          count,
          display: count > 0 ? `₹${totalAmount.toLocaleString()}` : ''
        };
      }
    },
    {
      id: 'viewCalculation',
      label: 'View Calculation',
      icon: 'bx-calculator',
      shortcut: 'F10',
      onClick: onCalculationClick,
      getValue: () => ({ count: 0, display: '' })
    }
  ], [state, onCustomerClick, onSalesPersonClick, onCouponClick, onDiscountClick, onPaymentClick, onOldGoldClick, onExchangeClick, onRedemptionClick, onRewardPointsClick, onCalculationClick]);

  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if any input field is focused to avoid interfering with typing
      const activeElement = document.activeElement as HTMLElement;
      const isInputFocused = activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.tagName === 'SELECT' ||
        activeElement?.contentEditable === 'true';

      if (isInputFocused) return;

      // Handle F1-F12 keys
      const functionKeys = ['F1', 'F2', 'F3', 'F7', 'F8', 'F9', 'F10', 'F12'];
      const pressedKey = event.code;

      if (functionKeys.includes(pressedKey)) {
        event.preventDefault();
        const tabConfig = tabConfigs.find(tab => tab.shortcut === pressedKey);
        if (tabConfig) {
          tabConfig.onClick();
        }
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [tabConfigs]);

  // Render a single tab button
  const renderTabButton = (tab: TabConfig) => {
    const { count } = tab.getValue();
    const isSelected = count > 0;

    return (
      <Button
        key={tab.id}
        variant={isSelected ? 'success' : 'outline-primary'}
        size="sm"
        onClick={tab.onClick}
        className="tab-button"
      >
        <i className={`bx ${tab.icon} me-1`}></i>
        {tab.label}
      </Button>
    );
  };

  return (
    <div className="sales-entry-tabs">
      <div className="d-flex flex-wrap gap-1">
        {tabConfigs.map(renderTabButton)}
      </div>
    </div>
  );
};